import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import {
	FormControl,
	Validators,
	ValidatorFn,
	AbstractControl,
	ValidationErrors,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogTitle,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	switchMap,
	startWith,
	takeUntil,
	filter,
} from 'rxjs/operators';
import {
	ApiService,
	DeviceRemovalActions,
} from 'src/app/services/api.services';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import { Device } from 'src/app/models/data/device';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIcon } from '@angular/material/icon';
import {
	MatFormField,
	MatPrefix,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';

export interface RemoveDeviceDialogData {
	deviceId: string;
	deviceName?: string;
	device: Device;
}

// Custom validator that requires non-empty trimmed string
function trimmedRequiredValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (value == null || value.trim().length === 0) {
			return { required: true };
		}
		return null;
	};
}

enum DialogStep {
	SelectAction = 1,
	SelectDevice = 2,
	SendHaspCode = 3,
	TransferComplete = 4,
}

@Component({
	selector: 'app-remove-device-dialog',
	templateUrl: './remove-device-dialog.component.html',
	styleUrls: [
		'./remove-device-dialog.component.scss',
		'../../ui/common/main.scss',
	],
	imports: [
		MatDialogTitle,
		CdkScrollable,
		MatDialogContent,
		MatIcon,
		MatFormField,
		MatPrefix,
		MatInput,
		FormsModule,
		MatAutocompleteTrigger,
		ReactiveFormsModule,
		MatSuffix,
		MatAutocomplete,
		MatOption,
		MatDialogActions,
		MatProgressSpinner,
		AsyncPipe,
	],
})
export class RemoveDeviceDialogComponent implements OnInit, OnDestroy {
	selectedAction: DeviceRemovalActions =
		DeviceRemovalActions.requestEndAllAccesses;
	isLoading: boolean = false;
	currentStep: DialogStep = DialogStep.SelectAction;

	DeviceRemovalActions = DeviceRemovalActions;
	DialogStep = DialogStep;

	// Device selection properties
	deviceSearchControl: FormControl = new FormControl('');
	filteredDevices?: Observable<Device[]>;
	selectedDevice?: Device;
	private destroy$ = new Subject<void>();

	// HASP code form properties
	haspFirstNameControl: FormControl = new FormControl('', [
		trimmedRequiredValidator(),
	]);
	haspLastNameControl: FormControl = new FormControl('', [
		trimmedRequiredValidator(),
	]);
	haspEmailControl: FormControl = new FormControl('', [Validators.email]);

	// Transfer complete properties
	transferredDeviceSerialNumber?: string;
	sourceDeviceHaspCode?: string;
	sourceDeviceType?: string;
	sourceDeviceHaspCodeCopied: boolean = false;
	targetDeviceHaspCode?: string;
	targetDeviceType?: string;
	targetDeviceHaspCodeCopied: boolean = false;

	constructor(
		private dialogRef: MatDialogRef<RemoveDeviceDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: RemoveDeviceDialogData,
		private apiService: ApiService,
		private bannerMessageService: BannerMessageService
	) {}

	ngOnInit() {
		this.filteredDevices = this.deviceSearchControl.valueChanges.pipe(
			startWith(''),
			debounceTime(300),
			distinctUntilChanged(),
			filter((keyword) => {
				// Skip if it's a Device object (user selected something)
				if (keyword && typeof keyword !== 'string') {
					return false;
				}
				// Only search if empty or 3+ characters
				return keyword.length === 0 || keyword.length >= 3;
			}),
			switchMap((keyword) => this.searchDevices(keyword))
		);

		// Clear selected device when user types in the search field
		this.deviceSearchControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (typeof value === 'string') {
					this.selectedDevice = undefined;
				}
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	async searchDevices(keyword: string | Device): Promise<Device[]> {
		if (!keyword || typeof keyword !== 'string') {
			// If no keyword, load initial results
			try {
				return await this.apiService.getTransferEligibleDevices(
					this.data.deviceId,
					undefined,
					0,
					10
				);
			} catch (err) {
				console.error('Error loading devices:', err);
				return [];
			}
		}

		try {
			return await this.apiService.getTransferEligibleDevices(
				this.data.deviceId,
				keyword
			);
		} catch (err) {
			console.error('Error searching devices:', err);
			return [];
		}
	}

	displayDeviceSerialNumber(device: Device | null): string {
		return device?.serialNumber || '';
	}

	onDeviceSelected(device: Device): void {
		this.selectedDevice = device;
	}

	onActionSelected(action: DeviceRemovalActions): void {
		this.selectedAction = action;
	}

	onCancel(): void {
		if (this.currentStep === DialogStep.TransferComplete) {
			// On transfer complete step, close the dialog
			this.dialogRef.close({
				action: this.selectedAction,
				deviceId: this.data.deviceId,
				transferToDeviceId: this.selectedDevice?.id,
				success: true,
			});
		} else if (this.currentStep === DialogStep.SendHaspCode) {
			// Go back to step 2
			this.currentStep = DialogStep.SelectDevice;
		} else if (this.currentStep === DialogStep.SelectDevice) {
			// Go back to step 1
			this.currentStep = DialogStep.SelectAction;
			this.selectedDevice = undefined;
			this.deviceSearchControl.setValue('');
		} else {
			this.dialogRef.close(false);
		}
	}

	isWallmount(device: Device): boolean {
		return device?.deviceType?.toLowerCase()?.includes('wall mount') ?? false;
	}

	async onContinue(): Promise<void> {
		if (this.isLoading) return;

		// If "Transfer all access" is selected and we're on step 1, go to step 2
		if (
			this.currentStep === DialogStep.SelectAction &&
			this.selectedAction === DeviceRemovalActions.requestTransferAccesses
		) {
			this.moveToDeviceSelection();
			return;
		}

		// If on step 2 (device selection) and device is selected
		if (this.currentStep === DialogStep.SelectDevice && this.selectedDevice) {
			await this.handleDeviceSelection();
			return;
		}

		// If on step 3 (HASP code) and form is valid
		if (this.currentStep === DialogStep.SendHaspCode) {
			await this.handleHaspCodeSubmission();
			return;
		}

		// If on step 4 (Transfer Complete), close the dialog
		if (this.currentStep === DialogStep.TransferComplete) {
			this.closeDialogWithTransferSuccess();
			return;
		}

		// Otherwise, proceed with the removal action
		await this.handleRemovalAction();
	}

	moveToDeviceSelection(): void {
		this.currentStep = DialogStep.SelectDevice;
	}

	async handleDeviceSelection(): Promise<void> {
		// If both devices are wallmounts, transfer access without hasp code
		if (
			this.isWallmount(this.selectedDevice!) &&
			this.isWallmount(this.data.device)
		) {
			await this.transferAccessForWallmount();
		} else {
			// If either device is not a wallmount, go to the HASP code step
			this.moveToHaspCodeStep();
		}
	}

	async transferAccessForWallmount(): Promise<void> {
		this.isLoading = true;

		try {
			// Call API to transfer device access (no hasp code for wallmounts)
			const response = await this.apiService.transferDeviceAccess(
				this.data.deviceId,
				this.selectedDevice!.id
			);

			// Store transferred device info and hasp code (if any)
			this.transferredDeviceSerialNumber = this.selectedDevice!.serialNumber;

			// Go to transfer complete step
			this.currentStep = DialogStep.TransferComplete;
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-errorTransferringDeviceAccess:An error occurred while transferring device access. Please try again.`
			);

			this.dialogRef.close({
				action: this.selectedAction,
				deviceId: this.data.deviceId,
				success: false,
			});
		} finally {
			this.isLoading = false;
		}
	}

	moveToHaspCodeStep(): void {
		this.currentStep = DialogStep.SendHaspCode;
	}

	async handleHaspCodeSubmission(): Promise<void> {
		this.isLoading = true;

		try {
			// Trim all form values before sending to API
			const trimmedFirstName = this.haspFirstNameControl.value?.trim() || '';
			const trimmedLastName = this.haspLastNameControl.value?.trim() || '';
			const trimmedEmail = this.haspEmailControl.value?.trim() || '';

			// Call API to transfer device access with HASP code generation
			const response = await this.apiService.transferDeviceAccess(
				this.data.deviceId,
				this.selectedDevice!.id,
				trimmedFirstName,
				trimmedLastName,
				trimmedEmail
			);

			// Store transferred device info and hasp code from API response
			this.transferredDeviceSerialNumber = this.selectedDevice?.serialNumber;
			this.sourceDeviceType = response.sourceDeviceType;
			this.sourceDeviceHaspCode = response.sourceDeviceHaspCode;
			this.targetDeviceType = response.targetDeviceType;
			this.targetDeviceHaspCode = response.targetDeviceHaspCode;

			this.sourceDeviceHaspCodeCopied = false;
			this.targetDeviceHaspCodeCopied = false;

			// Go to transfer complete step
			this.currentStep = DialogStep.TransferComplete;
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-errorTransferringDeviceAccess:An error occurred while transferring device access. Please try again.`
			);

			this.dialogRef.close({
				action: this.selectedAction,
				deviceId: this.data.deviceId,
				success: false,
			});
		} finally {
			this.isLoading = false;
		}
	}

	closeDialogWithTransferSuccess(): void {
		this.dialogRef.close({
			action: this.selectedAction,
			deviceId: this.data.deviceId,
			transferToDeviceId: this.selectedDevice?.id,
			haspFirstName: this.haspFirstNameControl.value?.trim() || '',
			haspLastName: this.haspLastNameControl.value?.trim() || '',
			haspEmail: this.haspEmailControl.value?.trim() || '',
			success: true,
		});
	}

	async handleRemovalAction(): Promise<void> {
		this.isLoading = true;

		try {
			// Map the dialog action to the appropriate API action
			let apiAction: DeviceRemovalActions;
			switch (this.selectedAction) {
				case DeviceRemovalActions.requestEndAllAccesses:
					apiAction = DeviceRemovalActions.requestEndAllAccesses;
					break;
				case DeviceRemovalActions.request:
					apiAction = DeviceRemovalActions.request;
					break;
				case DeviceRemovalActions.requestTransferAccesses:
					apiAction = DeviceRemovalActions.requestTransferAccesses;
					break;
				default:
					apiAction = DeviceRemovalActions.request;
			}

			await this.apiService.requestDeviceRemovalAction(
				this.data.deviceId,
				apiAction
			);

			// Send success message
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-deviceRemovalRequested:Device removal requested.`
			);

			this.dialogRef.close({
				action: this.selectedAction,
				deviceId: this.data.deviceId,
				transferToDeviceId: this.selectedDevice?.id,
				success: true,
			});
		} catch (err) {
			// Send error message
			this.bannerMessageService.sendMessage(
				$localize`:@@deviceAbout-errorRequestingDeviceRemoval:An error occurred while requesting device removal. Please try again.`
			);

			this.dialogRef.close({
				action: this.selectedAction,
				deviceId: this.data.deviceId,
				success: false,
			});
		} finally {
			this.isLoading = false;
		}
	}

	isContinueDisabled(): boolean {
		if (this.isLoading) return true;

		// On device selection step, require a device to be selected
		if (this.currentStep === DialogStep.SelectDevice) {
			return !this.selectedDevice;
		}

		// On HASP code step, require first name, last name, and valid email if provided
		if (this.currentStep === DialogStep.SendHaspCode) {
			return (
				this.haspFirstNameControl.invalid ||
				this.haspLastNameControl.invalid ||
				this.haspEmailControl.invalid
			);
		}

		return false;
	}

	getContinueButtonText(): string {
		// Show "Done" if on transfer complete step
		if (this.currentStep === DialogStep.TransferComplete) {
			return $localize`:@@removeDeviceDialog-doneButton:Done`;
		}

		// Show "Transfer Access" if on HASP code step or device selection with wallmount selected
		if (this.currentStep === DialogStep.SendHaspCode) {
			return $localize`:@@removeDeviceDialog-transferAccessButton:Transfer Access`;
		}

		if (
			this.currentStep === DialogStep.SelectDevice &&
			this.selectedDevice &&
			this.isWallmount(this.selectedDevice)
		) {
			return $localize`:@@removeDeviceDialog-transferAccessButton:Transfer Access`;
		}

		// Otherwise show "Continue"
		return $localize`:@@removeDeviceDialog-continueButton:Continue`;
	}

	copyTargetDeviceHaspCode(): void {
		if (this.targetDeviceHaspCode) {
			navigator.clipboard.writeText(this.targetDeviceHaspCode);
			this.targetDeviceHaspCodeCopied = true;
		}
	}

	copySourceDeviceHaspCode(): void {
		if (this.sourceDeviceHaspCode) {
			navigator.clipboard.writeText(this.sourceDeviceHaspCode);
			this.sourceDeviceHaspCodeCopied = true;
		}
	}
}
